import { Box, styled } from '@mui/material';
import { ChatHeader } from './components/ChatHeader';
import { DisclaimerWarning } from './components/DisclaimerWarning';
import { MessageInput } from './components/MessageInput';
import { MessageList } from './components/MessageList';
import { useConsumeSeedChatPrompt } from './hooks/chatActionHooks';
import { useChat } from './hooks/useChat';
import { useAgentErrorEffects, useCombinedAgentHealth } from './hooks/useAgentHealthStatus';
import { useAutoReconnect } from './hooks/useAutoReconnect';
import { AgentStatus } from './components/AgentStatusAlert';
import { AgentTenantMismatchAlert } from './components/AgentTenantMismatchAlert';
import { ChatErrorAlert } from './components/ChatErrorAlert';

const StyledChatContainer = styled(Box)`
  --chat-container-height: calc(100dvh - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px));
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  height: var(--chat-container-height);
  max-height: var(--chat-container-height);
  display: grid;
  /* grid rows: header, messages, agent status, tenant mismatch, error, input */
  grid-template-rows: min-content 1fr min-content auto auto auto;
`;

const ChatContent = styled(Box)`
  height: 100%;
  min-height: 100%;
  position: relative;
`;

const InputContainer = styled(Box)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(2, 3)};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  background: ${({ theme }) => theme.palette.background.paper};
  max-height: fit-content;

  & input[type='text'],
  & textarea {
    font-size: 16px !important; // prevents zooming in on input field
  }
`;

function ChatBot() {
  const { messages, isTyping, sendMessage, isCancelling, onCancelGeneration } = useChat();
  const healthStatus = useCombinedAgentHealth();
  const { isAutoReconnecting } = useAutoReconnect();
  useAgentErrorEffects(healthStatus);
  useConsumeSeedChatPrompt(sendMessage);
  return (
    <StyledChatContainer className='foresight-assistant'>
      <ChatHeader />
      <ChatContent>
        <MessageList messages={messages} isTyping={isTyping} />
      </ChatContent>
      <AgentStatus status={healthStatus} isAutoReconnecting={isAutoReconnecting} />
      <AgentTenantMismatchAlert />
      <ChatErrorAlert />
      <InputContainer>
        <MessageInput
          isCancelling={isCancelling}
          isDisabled={healthStatus === 'error'}
          isReceiving={isTyping}
          onCancelGeneration={onCancelGeneration}
          onSendMessage={sendMessage}
          placeholder='Ask about any company, fund or KPI'
        />
        <DisclaimerWarning />
      </InputContainer>
    </StyledChatContainer>
  );
}

export default ChatBot;
